import React, { useRef, useState } from 'react'
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StepMode, { ModeChoice } from './StepMode'
import StepName from './StepName'
import StepChanges from './StepChanges'
import StepReview from './StepReview'
import { BatchRegenerateState, changesMissing } from './regenerateShared'

type StepKey = 'mode' | 'name' | 'changes' | 'review'

const STEP_TITLES: Record<StepKey, string> = {
  mode: 'Overwrite or new set',
  name: 'Name the new set',
  changes: 'What would you like to change?',
  review: 'Review and generate',
}

export interface RegenerateWizardProps {
  batch: BatchRegenerateState
  customer: string
  job: string
  /** Generate: the page submits, reports and leaves. */
  onGenerate: () => void
  /** Back from the first step: return to Tables. */
  onCancel: () => void
}

/**
 * Regenerate Tables as a walkthrough (#1809), one question per step, modelled
 * on the Gen AI wizard: a step caption, a progress bar, Back on every step and
 * Next blocked until the step is valid. "Overwrite or new set" shows only for
 * the user's own My Tables folder; choosing Overwrite skips "Name the new set".
 */
const RegenerateWizard: React.FC<RegenerateWizardProps> = ({ batch, customer, job, onGenerate, onCancel }) => {
  const [index, setIndex] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [modeChoice, setModeChoice] = useState<ModeChoice | null>(null)

  const steps = (['mode', 'name', 'changes', 'review'] as StepKey[]).filter(s =>
    s === 'mode' ? batch.canOverwrite : s === 'name' ? !batch.overwrite : true)
  const step = steps[Math.min(index, steps.length - 1)]

  const missing: string[] =
    step === 'mode' ? (modeChoice ? [] : ['choose one'])
    : step === 'name' ? (batch.prefix.trim() ? [] : ['add a prefix'])
    : step === 'changes' ? changesMissing(batch)
    : []

  const handleChoose = (choice: ModeChoice) => {
    setModeChoice(choice)
    batch.setOverwrite(choice === 'overwrite')
  }
  // Every step starts at its top: Review used to open scrolled to where the
  // changes step was left (#1809).
  const goTo = (next: number) => {
    setIndex(next)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }
  const handleBack = () => (index === 0 ? onCancel() : goTo(index - 1))
  const handleNext = () => goTo(index + 1)

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 1000, mx: 'auto', px: 3, pt: 2, pb: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography data-testid="regenerate-step-caption" variant="caption" color="text.secondary">
          Step {index + 1} of {steps.length} · {STEP_TITLES[step]}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={((index + 1) / steps.length) * 100}
          color="info"
          sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
        />
      </Box>

      <Box ref={scrollRef} data-testid="regenerate-step-body" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {step === 'mode' && <StepMode choice={modeChoice} onChoose={handleChoose} />}
        {step === 'name' && <StepName batch={batch} />}
        {step === 'changes' && <StepChanges batch={batch} customer={customer} job={job} />}
        {step === 'review' && <StepReview batch={batch} />}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} disabled={batch.loading}>
          Back
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          {missing.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              To continue, {missing.join(', ')}.
            </Typography>
          )}
          {step === 'review' ? (
            <Button
              variant="contained"
              color="info"
              startIcon={<PlayArrowIcon />}
              onClick={onGenerate}
              disabled={batch.loading || batch.sourceTables.length === 0}
            >
              {batch.loading ? 'Generating…' : 'Go'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="info"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={missing.length > 0}
            >
              {step === 'changes' ? 'Review' : 'Next'}
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  )
}

export default RegenerateWizard
